import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getIncidentById } from '@/api/incidents';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface IncidentTabProps {
  incidentId: string;
}

const IncidentTab: React.FC<IncidentTabProps> = ({ incidentId }) => {
  const { t } = useTranslation(['incidentReporting', 'common']);
  const { firestoreUser } = useAuth();

  const { data: incident, isLoading, error } = useQuery({
    queryKey: ['incident', incidentId],
    queryFn: () => getIncidentById(incidentId, firestoreUser!),
    enabled: !!incidentId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !incident) {
    return (
      <Card>
        <Empty description={t('incidentReporting:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('incidentReporting:fields.title')}>
          {incident.title}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.description')}>
          {incident.description}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.type')}>
          {t(`incidentReporting:types.${incident.type}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.priority')}>
          {t(`incidentReporting:priority.${incident.priority}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.status')}>
          {t(`incidentReporting:status.${incident.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.reportedBy')}>
          {incident.reportedBy.name}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('incidentReporting:fields.reportedAt')}>
          {dayjs(incident.reportedAt).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        
        {incident.resolvedAt && (
          <Descriptions.Item label={t('incidentReporting:fields.resolvedAt')}>
            {dayjs(incident.resolvedAt).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
        )}
        
        {incident.assignedTo && (
          <Descriptions.Item label={t('incidentReporting:fields.assignedTo')}>
            {incident.assignedTo.name}
          </Descriptions.Item>
        )}
        
        {incident.block && (
          <Descriptions.Item label={t('incidentReporting:fields.block')}>
            {incident.block.name}
          </Descriptions.Item>
        )}
        
        {incident.apartmentUnit && (
          <Descriptions.Item label={t('incidentReporting:fields.apartmentUnit')}>
            {incident.apartmentUnit.name}
          </Descriptions.Item>
        )}
        
        {incident.amenity && (
          <Descriptions.Item label={t('incidentReporting:fields.amenity')}>
            {incident.amenity.name}
          </Descriptions.Item>
        )}
        
        {incident.attachments && incident.attachments.length > 0 && (
          <Descriptions.Item label={t('incidentReporting:fields.attachments')}>
            {incident.attachments.length} {t('common:items')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default IncidentTab; 